package api

import api.dataclasses.ApiResult
import api.dataclasses.login.LoginRequest
import api.dataclasses.login.LoginResponse
import api.dataclasses.Session
import api.dataclasses.deliveries.Delivery
import api.dataclasses.deliveries.DeliveryDetailed
import api.dataclasses.inbound.InboundReceipt
import api.dataclasses.inbound.InboundReceiptDetailed
import api.dataclasses.inbound.InboundReceiptLine
import api.dataclasses.outbound.OutboundOrder
import api.dataclasses.outbound.OutboundOrderDetailed
import api.dataclasses.outbound.OutboundOrderline
import api.dataclasses.stock.StockObject
import ics.handlers.RequestHandler
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.browser.window
import react.StateSetter
import kotlin.js.Date


interface ApiService {
    companion object {
        /**
         * creates a new session in the sessionStorage
         */
        private fun createLocalSession(session: Session) {
            window.sessionStorage.setItem(
                "username", session.username
            )
            window.sessionStorage.setItem(
                "owner", session.owner
            )
            window.sessionStorage.setItem(
                "token", session.token
            )
            window.sessionStorage.setItem(
                "refreshToken", session.refreshToken
            )
        }

        /**
         * clears the local sessionStorage
        */
        private fun clearLocalSession() {
            window.sessionStorage.clear()
        }

        /**
         *  checks if there is still an open session in the sessionStorage
         */
        fun checkLocalSession(): Session? {
            val usernameSession = window.sessionStorage.getItem(
                "username"
            )
            val ownerSession = window.sessionStorage.getItem(
                "owner"
            )
            val tokenSession = window.sessionStorage.getItem(
                "token"
            )
            val refreshTokenSession = window.sessionStorage.getItem(
                "refreshToken"
            )
            return if (!usernameSession.isNullOrEmpty() && !ownerSession.isNullOrEmpty() && !tokenSession.isNullOrEmpty() && !refreshTokenSession.isNullOrEmpty()) {
                return Session(
                    usernameSession,
                    ownerSession,
                    tokenSession,
                    refreshTokenSession
                )
            }
            else null
        }

        /**
         * login and get a new session
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        suspend fun login(credentials: LoginRequest): ApiResult<Session?> {
            var returnValue: ApiResult<Session?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.login,
                    unknownKeys = true
                ) {
                    method = HttpMethod.Post
                    contentType(ContentType.Application.Json)
                    accept(ContentType.Application.Json)
                    setBody(credentials)
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<LoginResponse>()

                            val session = Session(
                                res.username,
                                res.owner,
                                res.token,
                                res.refreshToken
                            )

                            this.createLocalSession(session)

                            returnValue = ApiResult(
                                true,
                                session,
                                null
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }

                return returnValue

            } catch (e: Throwable) {
                console.error(e)
                throw (e)
            }
        }

        /**
         * clears the session from the browser and resets the application
         */
        fun logout(sessionSetter: StateSetter<Session?>) {
            this.clearLocalSession()
            sessionSetter(null)
        }

        /**
         * gets a new token set for new requests
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        private suspend fun refreshToken(session: Session): ApiResult<Session?> {
            var returnValue: ApiResult<Session?> = ApiResult()

            try {
                clearLocalSession()

                RequestHandler(
                    APIEndpoints.refreshTokenUrl,
                    unknownKeys = true
                ) {
                    method = HttpMethod.Post
                    bearerAuth(session.refreshToken)
                    contentType(ContentType.Application.Json)
                    accept(ContentType.Application.Json)
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val body = response.body<LoginResponse>()

                            val newSession = Session(
                                body.username,
                                body.owner,
                                body.token,
                                body.refreshToken
                            )

                            returnValue = ApiResult(
                                true,
                                newSession,
                                null
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
                throw e
            }
            return returnValue
        }

        /**
         * get all the stock
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        suspend fun getStockList(session: Session, sessionSetter: StateSetter<Session?>?): ApiResult<Array<StockObject>?> {
            var returnValue: ApiResult<Array<StockObject>?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.stockListURL,
                    unknownKeys = true
                ){
                    method = HttpMethod.Get
                    accept(ContentType.Application.Json)
                    bearerAuth(session.token)
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<Array<StockObject>>()
                            returnValue = ApiResult(
                                true,
                                res
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val refreshedSession = this.refreshToken(session)
                            if (refreshedSession.success) {
                                val tempValue = this.getStockList(refreshedSession.data!!, sessionSetter)
                                tempValue.refreshedSession = refreshedSession.refreshedSession
                                returnValue = tempValue
                            } else {
                                sessionSetter?.let {
                                    it(null)
                                }
                            }
                        }
                        else -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
            }

            return returnValue
        }

        /**
         * get all the inbound receipts
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        suspend fun getInboundReceipts(session: Session, sessionSetter: StateSetter<Session?>?, fromDate: Date? = null, endDate: Date? = null): ApiResult<Array<InboundReceipt>?> {
            var returnValue: ApiResult<Array<InboundReceipt>?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.receiveOrdersListURL,
                    unknownKeys = true
                ){
                    method = HttpMethod.Get
                    accept(ContentType.Application.Json)
                    bearerAuth(session.token)

                    if (fromDate != null) parameter("StartDate", fromDate.toISOString())
                    if (endDate != null) parameter("EndDate", endDate.toISOString())
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<Array<InboundReceipt>>()
                            returnValue = ApiResult(
                                true,
                                res
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val refreshedSession = this.refreshToken(session)
                            if (refreshedSession.success) {
                                val tempValue = this.getInboundReceipts(refreshedSession.data!!, sessionSetter, fromDate, endDate)
                                tempValue.refreshedSession = refreshedSession.refreshedSession
                                returnValue = tempValue
                            } else sessionSetter?.let {
                                it(null)
                            }
                        }
                        else -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
            }

            return returnValue
        }

        /**
         * gets all the inbound receipt orderlines for a inbound receipt
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        private suspend fun getInboundReceiptOrderlines(session: Session, sessionSetter: StateSetter<Session?>?, recordId: Int): ApiResult<Array<InboundReceiptLine>?> {
            var returnValue: ApiResult<Array<InboundReceiptLine>?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.receiveOrderlinesURL.plus(recordId.toString()),
                    unknownKeys = true
                ){
                    method = HttpMethod.Get
                    accept(ContentType.Application.Json)
                    bearerAuth(session.token)
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<Array<InboundReceiptLine>>()
                            returnValue = ApiResult(
                                true,
                                res
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val refreshedSession = this.refreshToken(session)
                            if (refreshedSession.success) {
                                val tempValue = this.getInboundReceiptOrderlines(refreshedSession.data!!, sessionSetter, recordId)
                                tempValue.refreshedSession = refreshedSession.refreshedSession
                                returnValue = tempValue
                            } else sessionSetter?.let {
                                it(null)
                            }
                        }
                        else -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
            }

            return returnValue
        }

        /**
         * gets a detailed view of the inbound order including the orderlines
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        suspend fun getInboundOrderDetailed(session: Session, sessionSetter: StateSetter<Session?>?, recordId: Int): ApiResult<InboundReceiptDetailed?> {
            var returnValue: ApiResult<InboundReceiptDetailed?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.receiveOrdersDetailURL.plus(recordId.toString()),
                    unknownKeys = true
                ){
                    method = HttpMethod.Get
                    accept(ContentType.Application.Json)
                    bearerAuth(session.token)
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<InboundReceiptDetailed>()

                            val lines = this.getInboundReceiptOrderlines(session, sessionSetter, recordId)

                            res.lines = if (lines.success) lines.data ?: arrayOf() else arrayOf()

                            returnValue = ApiResult(
                                true,
                                res
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val refreshedSession = this.refreshToken(session)
                            if (refreshedSession.success) {
                                val tempValue = this.getInboundOrderDetailed(refreshedSession.data!!, sessionSetter, recordId)
                                tempValue.refreshedSession = refreshedSession.refreshedSession
                                returnValue = tempValue
                            } else sessionSetter?.let {
                                it(null)
                            }
                        }
                        else -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
            }

            return returnValue
        }

        /**
         * Gets all outbound orders
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        suspend fun getOutboundOrderList(session: Session, sessionSetter: StateSetter<Session?>?, fromDate: Date? = null, endDate: Date? = null) : ApiResult<Array<OutboundOrder>?> {
            var returnValue: ApiResult<Array<OutboundOrder>?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.pickOrdersListURL,
                    unknownKeys = true
                ){
                    method = HttpMethod.Get
                    accept(ContentType.Application.Json)
                    bearerAuth(session.token)

                    if (fromDate != null) parameter("StartDate", fromDate.toISOString())
                    if (endDate != null) parameter("EndDate", endDate.toISOString())

                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<Array<OutboundOrder>>()
                            returnValue = ApiResult(
                                true,
                                res
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val refreshedSession = this.refreshToken(session)
                            if (refreshedSession.success) {
                                val tempValue = this.getOutboundOrderList(refreshedSession.data!!, sessionSetter)
                                tempValue.refreshedSession = refreshedSession.refreshedSession
                                returnValue = tempValue
                            } else sessionSetter?.let {
                                it(null)
                            }
                        }
                        else -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
            }

            return returnValue
        }

        /**
         *  gets the orderlines for an outbound order
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        private suspend fun getOutboundOrderlines(session: Session, sessionSetter: StateSetter<Session?>?, recordId: Int): ApiResult<Array<OutboundOrderline>?> {
            var returnValue: ApiResult<Array<OutboundOrderline>?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.pickOrderlinesURL.plus(recordId.toString()),
                    unknownKeys = true
                ){
                    method = HttpMethod.Get
                    accept(ContentType.Application.Json)
                    bearerAuth(session.token)
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<Array<OutboundOrderline>>()
                            returnValue = ApiResult(
                                true,
                                res
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val refreshedSession = this.refreshToken(session)
                            if (refreshedSession.success) {
                                val tempValue = this.getOutboundOrderlines(refreshedSession.data!!, sessionSetter, recordId)
                                tempValue.refreshedSession = refreshedSession.refreshedSession
                                returnValue = tempValue
                            } else sessionSetter?.let {
                                it(null)
                            }
                        }
                        else -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
            }

            return returnValue
        }

        /**
         * gets a detailed view of the outbound order including the orderlines
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        suspend fun getOutboundOrderDetailed(session: Session, sessionSetter: StateSetter<Session?>?, recordId: Int): ApiResult<OutboundOrderDetailed?> {
            var returnValue: ApiResult<OutboundOrderDetailed?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.pickOrdersDetailURL.plus(recordId.toString()),
                    unknownKeys = true
                ){
                    method = HttpMethod.Get
                    accept(ContentType.Application.Json)
                    bearerAuth(session.token)
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<OutboundOrderDetailed>()

                            val lines = this.getOutboundOrderlines(session, sessionSetter, recordId)

                            res.lines = if (lines.success) lines.data ?: arrayOf() else arrayOf()

                            returnValue = ApiResult(
                                true,
                                res
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val refreshedSession = this.refreshToken(session)
                            if (refreshedSession.success) {
                                val tempValue = this.getOutboundOrderDetailed(refreshedSession.data!!, sessionSetter, recordId)
                                tempValue.refreshedSession = refreshedSession.refreshedSession
                                returnValue = tempValue
                            } else sessionSetter?.let {
                                it(null)
                            }
                        }
                        else -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
            }

            return returnValue
        }

        /**
         * gets all deliveries as a list
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        suspend fun getDeliveriesList(session: Session, sessionSetter: StateSetter<Session?>?, fromDate: Date? = null, endDate: Date? = null): ApiResult<Array<Delivery>?> {
            var returnValue: ApiResult<Array<Delivery>?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.deliveryOrdersListURL,
                    unknownKeys = true
                ){
                    method = HttpMethod.Get
                    accept(ContentType.Application.Json)
                    bearerAuth(session.token)

                    if (fromDate != null) parameter("StartDate", fromDate.toISOString())
                    if (endDate != null) parameter("EndDate", endDate.toISOString())
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<Array<Delivery>>()
                            returnValue = ApiResult(
                                true,
                                res
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val refreshedSession = this.refreshToken(session)
                            if (refreshedSession.success) {
                                val tempValue = this.getDeliveriesList(refreshedSession.data!!, sessionSetter, fromDate, endDate)
                                tempValue.refreshedSession = refreshedSession.refreshedSession
                                returnValue = tempValue
                            } else {
                                sessionSetter?.let {
                                    it(null)
                                }
                            }
                        }
                        else -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
            }

            return returnValue
        }

        /**
         * gets all deliveries as a list
         *
         * @throws NoTransformationFoundException
         * @throws DoubleReceiveException
         */
        suspend fun getDeliveryDetailed(session: Session, sessionSetter: StateSetter<Session?>?, recordId: Int): ApiResult<DeliveryDetailed?> {
            var returnValue: ApiResult<DeliveryDetailed?> = ApiResult()

            try {
                RequestHandler(
                    APIEndpoints.deliveryOrdersDetailURL.plus(recordId.toString()),
                    unknownKeys = true
                ){
                    method = HttpMethod.Get
                    accept(ContentType.Application.Json)
                    bearerAuth(session.token)
                }.execute { response ->
                    when (response.status) {
                        HttpStatusCode.OK -> {
                            val res = response.body<DeliveryDetailed>()
                            returnValue = ApiResult(
                                true,
                                res
                            )
                        }

                        HttpStatusCode.Unauthorized -> {
                            val refreshedSession = this.refreshToken(session)
                            if (refreshedSession.success) {
                                val tempValue = this.getDeliveryDetailed(refreshedSession.data!!, sessionSetter, recordId)
                                tempValue.refreshedSession = refreshedSession.refreshedSession
                                returnValue = tempValue
                            } else {
                                sessionSetter?.let {
                                    it(null)
                                }
                            }
                        }
                        else -> {
                            val res = response.body<String>()
                            returnValue = ApiResult(
                                false,
                                null,
                                res.ifEmpty { "An unexpected error occurred" }
                            )
                        }
                    }
                }
            } catch (e: Throwable) {
                console.error(e)
            }

            return returnValue
        }
    }
}